import React, { useEffect, useState, useMemo } from 'react';

import { BookingInquiryGetQueryOrdering } from '@apis/bookingInquiryApis';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { Layout } from '@components/Layout';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import {
  cellFactory,
  Table,
  TableCellPropsUnion,
  TableSelectableCellProps,
} from '@components/Table/Table';
import { BookingsContainer } from '@components/utilities/Bookings/BookingsContainer';
import { Filter } from '@components/utilities/Bookings/Filter';
import { bookingsStrings } from '@data/bookings';
import { useBookingInquiryList } from '@hooks/queries/useBookingInquiryList';
import { useSearch } from '@hooks/useSearch';
import { bookingInquiryGetQuery, clientInquiryStatusMap } from '@models/BookingInquiry';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { formatDateTime } from '@utils/types';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, _common) => ({
  link: {
    color: theme.palette.primary.main,
  },
}));

const strings = createStrings({
  ja: {
    d2dNo: 'D2D番号',
    author: '申込者',
    search: '絞り込み',
    no: 'No',
    transmissionTime: '予約申込時間',
    shipper: 'SHIPPER（輸出者）',
    cnee: 'CNEE（輸入者）',
    status: 'ステータス',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    confirming: '確認中',
    canceled: 'キャンセル済み',
    bookingConfirmed: '予約確定',
    details: '詳細',
    customerNo: 'お客様の管理番号',
    reset: 'リセット',
    exportCSV: 'Export CSV',
    booking: '予約',
    selectOneItemAtLeast: '少なくとも1つのアイテムを選択してください',
  },
});

type ResourceBookingInquiryType = NonNullable<
  ReturnType<typeof useBookingInquiryList>['data']
>['results'][number];

// eslint-disable-next-line max-len
function mapOrdering(value: number): BookingInquiryGetQueryOrdering {
  return value === 1 ? '-created_at' : 'created_at';
}

export const BookingInquiryHistory: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const orderingOptions = useMemo(() => {
    return [
      {
        value: 1,
        label: strings.fromTheNewTime,
      },
      {
        value: 2,
        label: strings.fromTheOldTime,
      },
    ];
  }, []);

  const isMobile = useIsMobile();

  const { search, updateSearch } = useSearch(bookingInquiryGetQuery);
  const query = {
    ...search,
    d2d_no: search.d2d_no && `Y-${search.d2d_no}`,
    page: search.page ?? 1,
  };
  const bookingInquiryListQuery = useBookingInquiryList(query);
  const { data, isLoading, error } = bookingInquiryListQuery;

  const [exportCsvDialogOpen, setExportCsvDialogOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedIds([]);
  }, [data]);

  const allChecked = data
    ? data.results.map(({ d2d_no }) => d2d_no).every((d2d_no) => selectedIds?.includes(d2d_no))
    : false;

  const pageCount = 10;

  const headColumns: TableCellPropsUnion[] = [
    cellFactory.createCheckable(allChecked && selectedIds.length > 0, () => {
      if (allChecked) {
        setSelectedIds([]);
      } else {
        setSelectedIds(data ? data.results.map(({ d2d_no }) => d2d_no) : []);
      }
    }),
    {
      label: strings.no,
    },
    {
      label: strings.d2dNo,
    },
    {
      label: <></>,
    },
    {
      label: strings.transmissionTime,
      type: 'selectable',
      options: orderingOptions,
      onChange: (value) => {
        updateSearch({
          ...search,
          page: 0,
          ordering: mapOrdering(value as number),
        });
      },
    } as TableSelectableCellProps,
    {
      label: strings.customerNo,
    },
    {
      label: strings.author,
    },
    {
      label: strings.shipper,
    },
    {
      label: strings.cnee,
    },
    {
      label: strings.status,
    } as TableSelectableCellProps,
  ];

  function mapResourceToTableRowData(
    i: number,
    resourceItem: ResourceBookingInquiryType,
    classNames: {
      d2dNo: string;
      link: string;
    },
  ): TableCellPropsUnion[] {
    return [
      cellFactory.createCheckable(selectedIds.includes(resourceItem.d2d_no), () => {
        if (selectedIds.includes(resourceItem.d2d_no)) {
          setSelectedIds(selectedIds.filter((i) => i !== resourceItem.d2d_no));
        } else {
          setSelectedIds([...selectedIds, resourceItem.d2d_no]);
        }
      }),
      {
        label: i,
      },
      {
        label: <span className={classNames.d2dNo}>{resourceItem.d2d_no}</span>,
      },
      {
        label: (
          <span className={classes.whitespacePre}>
            <Link
              to={`${url('/utilities/Bookings/BookingInquiryHistoryDetails')}?d2dNo=${
                resourceItem.d2d_no
              }`}
              className={classNames.link}
            >
              {strings.details}
            </Link>
          </span>
        ),
      },
      {
        label: (
          <span className={classes.whitespacePre}>
            {resourceItem.created_at == null ? '-' : formatDateTime(resourceItem.created_at)}
          </span>
        ),
      },
      {
        label: <span className={classes.whitespacePre}>{resourceItem.customer_no || '-'}</span>,
      },
      {
        label: (
          <span className={classes.whitespacePre}>
            {`${resourceItem.user?.last_name} ${resourceItem.user?.first_name}` || '-'}
          </span>
        ),
      },
      {
        label: <span className={classes.whitespacePre}>{resourceItem.shipper || '-'}</span>,
      },
      {
        label: <span className={classes.whitespacePre}>{resourceItem.consignee || '-'}</span>,
      },
      {
        label: (
          <span className={classes.whitespacePre}>
            {clientInquiryStatusMap(resourceItem.status) || '-'}
          </span>
        ),
      },
    ];
  }

  const bodyRows = data?.results.map((result, i) => {
    return mapResourceToTableRowData(i + pageCount * ((search?.page ?? 1) - 1) + 1, result, {
      d2dNo: classes.whitespacePre,
      link: classes.link,
    });
  });

  return (
    <Layout
      loading={isLoading}
      title={bookingsStrings.bookingInquiryHistory}
      errors={[error]}
      breadcrumbs={['/utilities/Dashboard', '/utilities/Bookings/BookingInquiryHistory']}
    >
      <ExportCSVDialog
        open={exportCsvDialogOpen}
        onClose={() => setExportCsvDialogOpen(false)}
        baseUrl="/booking-inquiry/"
        query={query}
        selectedIds={selectedIds}
        subject={strings.booking}
      />

      <BookingsContainer>
        <Filter>
          <Filter.Field name="d2d_no" />
          <Filter.Field name="author" />
          <div style={{ width: '100%' }} />
          <Filter.Button type="submit" text={strings.search} />
          <Filter.Button type="reset" text={strings.reset} />
          <Filter.Button
            type="button"
            text={strings.exportCSV}
            onClick={() => setExportCsvDialogOpen(true)}
          />
        </Filter>

        <Table
          columnFixedUntil={isMobile ? undefined : 4}
          fixedWidths={isMobile ? undefined : [60, 60, 120, 60]}
        >
          <Table.Head>
            <Table.Row columns={headColumns} />
          </Table.Head>
          {bodyRows?.map((columns, index) => {
            return <Table.Row key={index} columns={columns} />;
          })}
        </Table>

        <div className={clsx(classes.flexInline, classes.justifyEnd)}>
          <PaginationWithSearchQuery query={bookingInquiryListQuery} pageSize={pageCount} />
        </div>
      </BookingsContainer>
    </Layout>
  );
};
